import z from "zod";

import { type BaseObject } from "../../evidence/base";
import { useSettingsContext } from "../../settings/context";
import { cn } from "../../utils/cn";
import { createId } from "../../utils/create-id";
import { DataFormAutoUpdate } from "./components/data-form-auto-update";
import { DataFormHeader } from "./components/data-form-header";
import { DataFormProvider } from "./components/data-form-provider";
import { DataFormContextProvider } from "./context/data-form-context-provider";
import { useDataSource } from "./hooks/data-source";
import { type DataFormProps } from "./types";

export function DataForm<
  TData extends TFieldValues,
  TFieldValues extends BaseObject,
>({
  itemId,

  header,
  toolbar,

  children,

  api,
  url,
  closeHref,
  detailUrlMapper = (entity, url) => `${url}/${entity.id}`,

  detailSchema,
  formSchema,

  readOnly = false,
  defaultMode,

  titleMapper = () => "Bez názvu",

  fetchData,
  onMutationSuccess,

  className,
  enableWebsocketUpdate,
  ...props
}: DataFormProps<TData, TFieldValues>) {
  const { form } = useSettingsContext();

  const enableAutoUpdate =
    enableWebsocketUpdate === true || form?.enableWebsocketUpdate === true;

  const defaultValues = {
    id: createId(),
    ...props.defaultValues,
  };

  const {
    mutation,
    query,
    formSchema: builtFormSchema,
  } = useDataSource({
    api,
    itemId,
    detailSchema,
    buildFormSchema: formSchema,
    defaultValues,
    fetchData,
    onMutationSuccess,
    mutation: props.mutation,
  });

  return (
    <DataFormContextProvider
      mutation={mutation}
      query={query}
      defaultValues={defaultValues}
      url={url}
      api={api}
      readOnly={
        typeof readOnly === "function"
          ? !!query.data && readOnly(query.data)
          : readOnly
      }
      itemId={itemId}
      defaultMode={defaultMode}
      detailUrlMapper={detailUrlMapper}
    >
      <DataFormProvider
        formSchema={builtFormSchema ?? z.any()}
        className={cn("flex flex-col overflow-hidden", className)}
      >
        {header !== undefined ? (
          header
        ) : (
          <DataFormHeader
            titleMapper={titleMapper}
            toolbar={toolbar}
            closeHref={
              typeof closeHref === "function" && query.data
                ? closeHref(query.data)
                : typeof closeHref === "string"
                  ? closeHref
                  : undefined
            }
          />
        )}

        <div className="grow overflow-y-auto">{children}</div>

        {enableAutoUpdate && <DataFormAutoUpdate />}
      </DataFormProvider>
    </DataFormContextProvider>
  );
}
